"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createContentReport,
  createAdminSystemNotifications,
  getAdminDashboard,
  getAdminActivitySeries,
  getAdminConversation,
  getAdminService,
  listAdminCommunities,
  listAdminConversationMessages,
  listAdminConversations,
  listAdminAuditLogs,
  listAdminNotificationBroadcasts,
  listAdminPosts,
  listAdminReports,
  listAdminServiceOptions,
  listAdminServices,
  listAdminUsers,
  moderateAdminMessage,
  moderateAdminPost,
  reorderAdminServiceCategories,
  resolveAdminReport,
  setAdminAccountStatus,
  setAdminCommunityState,
  setAdminConversationArchived,
  setAdminServiceStatus,
  setAdminSystemRole,
  upsertAdminService,
  upsertAdminServiceCategory,
} from "@/lib/admin/platform-api";
import type {
  AccountStatus,
  AdminMessageAction,
  AdminPostAction,
  AdminServiceCategoryUpsertInput,
  AdminServiceUpsertInput,
  ReportReason,
  ReportTargetType,
  ServiceAdminStatus,
  SystemRole,
} from "@/lib/admin/platform-types";
import { queryKeys } from "@/lib/query-keys";
import { MissingSupabaseConfigError } from "@/lib/supabase/env";

function retryUnlessMissingConfig(failureCount: number, error: unknown) {
  return error instanceof MissingSupabaseConfigError ? false : failureCount < 1;
}

export function useAdminDashboard(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminDashboard,
    queryFn: getAdminDashboard,
    enabled,
    retry: retryUnlessMissingConfig,
  });
}

export function useAdminActivitySeries(enabled: boolean, days: number) {
  return useQuery({
    queryKey: queryKeys.adminActivitySeries(days),
    queryFn: () => getAdminActivitySeries(days),
    enabled,
    retry: retryUnlessMissingConfig,
  });
}

export function useAdminUsers(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminUsers,
    queryFn: listAdminUsers,
    enabled,
    retry: retryUnlessMissingConfig,
  });
}

export function useSetAccountStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      status,
    }: {
      userId: string;
      status: AccountStatus;
    }) => setAdminAccountStatus(userId, status),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers });
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminDashboard });
    },
  });
}

export function useSetSystemRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      role,
    }: {
      userId: string;
      role: SystemRole | null;
    }) => setAdminSystemRole(userId, role),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers });
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminAccess });
    },
  });
}

export function useAdminPosts(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminPosts,
    queryFn: listAdminPosts,
    enabled,
    retry: retryUnlessMissingConfig,
  });
}

export function useModerateAdminPost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      postId,
      action,
    }: {
      postId: string;
      action: AdminPostAction;
    }) => moderateAdminPost(postId, action),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminPosts });
      await queryClient.invalidateQueries({ queryKey: queryKeys.feed });
      await queryClient.invalidateQueries({ queryKey: ["community-feed"] });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.adminDashboard,
      });
    },
  });
}

export function useAdminConversations(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminConversations,
    queryFn: listAdminConversations,
    enabled,
    retry: retryUnlessMissingConfig,
  });
}

export function useAdminConversation(conversationId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminConversation(conversationId),
    queryFn: () => getAdminConversation(conversationId),
    enabled: enabled && Boolean(conversationId),
    retry: retryUnlessMissingConfig,
  });
}

export function useAdminConversationMessages(
  conversationId: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.adminConversationMessages(conversationId),
    queryFn: () => listAdminConversationMessages(conversationId),
    enabled: enabled && Boolean(conversationId),
    retry: retryUnlessMissingConfig,
  });
}

export function useModerateAdminMessage(conversationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      messageId,
      action,
    }: {
      messageId: string;
      action: AdminMessageAction;
    }) => moderateAdminMessage(messageId, action),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.adminConversationMessages(conversationId),
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.adminConversations,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.adminConversation(conversationId),
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.adminAuditLogs,
      });
    },
  });
}

export function useSetAdminConversationArchived(conversationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (archived: boolean) =>
      setAdminConversationArchived(conversationId, archived),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.adminConversations,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.adminConversation(conversationId),
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.adminAuditLogs,
      });
    },
  });
}

export function useAdminAuditLogs(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminAuditLogs,
    queryFn: listAdminAuditLogs,
    enabled,
    retry: retryUnlessMissingConfig,
  });
}

export function useAdminNotificationBroadcasts(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminNotificationBroadcasts,
    queryFn: listAdminNotificationBroadcasts,
    enabled,
    retry: retryUnlessMissingConfig,
  });
}

export function useCreateSystemNotifications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      type: "system_announcement" | "system_message";
      title: string;
      body: string | null;
      targetPath: string;
      recipientIds: string[] | null;
    }) => createAdminSystemNotifications(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.adminNotificationBroadcasts,
      });
    },
  });
}

export function useAdminCommunitiesList(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminCommunities,
    queryFn: listAdminCommunities,
    enabled,
    retry: retryUnlessMissingConfig,
  });
}

export function useSetCommunityState() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      communityId,
      featured,
      suspended,
      archived,
    }: {
      communityId: string;
      featured?: boolean | null;
      suspended?: boolean | null;
      archived?: boolean | null;
    }) =>
      setAdminCommunityState(communityId, { featured, suspended, archived }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.adminCommunities,
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminDashboard });
    },
  });
}

export function useAdminServicesCatalog(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminServicesCatalog,
    queryFn: listAdminServices,
    enabled,
    retry: retryUnlessMissingConfig,
  });
}

export function useSetServiceStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      serviceId,
      status,
    }: {
      serviceId: string;
      status: ServiceAdminStatus;
    }) => setAdminServiceStatus(serviceId, status),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.adminServicesCatalog,
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.catalogServices });
    },
  });
}

export function useAdminServiceOptions(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminServiceOptions,
    queryFn: listAdminServiceOptions,
    enabled,
    retry: retryUnlessMissingConfig,
  });
}

export function useAdminService(serviceId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminService(serviceId ?? "new"),
    queryFn: () => getAdminService(serviceId!),
    enabled: enabled && Boolean(serviceId),
    retry: retryUnlessMissingConfig,
  });
}

export function useUpsertAdminService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: AdminServiceUpsertInput) => upsertAdminService(input),
    onSuccess: async (serviceId) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.adminServicesCatalog,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.adminServiceOptions,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.adminService(serviceId),
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.catalogServices });
      await queryClient.invalidateQueries({ queryKey: queryKeys.serviceCategories });
      await queryClient.invalidateQueries({ queryKey: queryKeys.myCustomers });
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminCustomers });
    },
  });
}

export function useUpsertAdminServiceCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: AdminServiceCategoryUpsertInput) =>
      upsertAdminServiceCategory(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.adminServiceOptions,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.adminServicesCatalog,
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.serviceCategories });
    },
  });
}

export function useReorderAdminServiceCategories() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderedIds: string[]) =>
      reorderAdminServiceCategories(orderedIds),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.adminServiceOptions,
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.serviceCategories });
    },
  });
}

export function useAdminReportsQueue(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminReportsQueue,
    queryFn: listAdminReports,
    enabled,
    retry: retryUnlessMissingConfig,
  });
}

export function useResolveReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      reportId,
      action,
    }: {
      reportId: string;
      action: "dismiss" | "hide" | "suspend_user";
    }) => resolveAdminReport(reportId, action),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.adminReportsQueue,
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminDashboard });
      await queryClient.invalidateQueries({ queryKey: queryKeys.feed });
    },
  });
}

export function useCreateContentReport() {
  return useMutation({
    mutationFn: (input: {
      targetType: ReportTargetType;
      targetId: string;
      reason: ReportReason;
      details?: string;
    }) => createContentReport(input),
  });
}
